import { Drawer } from '@/components/shared/Drawer'
import { assetPath } from '@/utils/assetPath'
import { useNavigate } from '@tanstack/react-router'
import type { AuthFlowMarker } from '@wtf/shared'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'
import { useEffect, lazy, Suspense } from 'react'
import { env } from '@/config/env'

const AdaptiveVideo = lazy(() => import('@/components/shared/AdaptiveVideo'))

interface AuthBottomSheetProps {
    isOpen: boolean
    onClose: () => void
    returnToPath?: string
}

export function AuthBottomSheet({
    isOpen,
    onClose,
    returnToPath, // page to return to after login/signup
}: AuthBottomSheetProps) {
    const navigate = useNavigate()
    const posthog = usePostHog()

    // Track when modal opens
    useEffect(() => {
        if (isOpen && posthog) {
            posthog.capture(AnalyticsEvents.AUTH_MODAL_OPENED, {
                return_path: returnToPath || 'none',
                timestamp: new Date().toISOString(),
            })
        }
    }, [isOpen, posthog, returnToPath])

    const handleLogin = () => {
        // Track the event
        if (posthog) {
            posthog.capture(AnalyticsEvents.AUTH_MODAL_LOGIN_CLICKED, {
                return_path: returnToPath || 'none',
                timestamp: new Date().toISOString(),
            })
        }

        // Existing code...
        if (returnToPath) {
            // Store auth flow marker
            const marker: AuthFlowMarker = {
                startPath: returnToPath,
                historyLength: window.history.length,
                timestamp: Date.now(),
            }
            sessionStorage.setItem('authFlowMarker', JSON.stringify(marker))
        }

        navigate({ to: '/login', search: { returnToPath } })
        onClose()
    }

    const handleSignup = () => {
        // Track the event
        if (posthog) {
            posthog.capture(AnalyticsEvents.AUTH_MODAL_SIGNUP_CLICKED, {
                return_path: returnToPath || 'none',
                timestamp: new Date().toISOString(),
            })
        }

        // Existing code...
        if (returnToPath) {
            // Store auth flow marker (same as login)
            const marker: AuthFlowMarker = {
                startPath: returnToPath,
                historyLength: window.history.length,
                timestamp: Date.now(),
            }
            sessionStorage.setItem('authFlowMarker', JSON.stringify(marker))
        }

        navigate({ to: '/register', search: { returnToPath } })
        onClose()
    }

    if (!isOpen) return null

    // Handle close when drawer state changes (e.g., outside click or drag down)
    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            onClose()
        }
    }

    // Empty div as triggerContent since we're controlling this drawer programmatically
    return (
        <Drawer
            open={isOpen}
            onOpenChange={handleOpenChange}
            triggerContent={null}
        >
            <div className="relative overflow-hidden rounded-t-2xl h-[65vh] min-h-[500px] max-h-[85vh] -m-2 md:-m-4">
                {/* Video background */}
                <div className="absolute inset-0 ">
                    <Suspense
                        fallback={
                            <div className="h-full w-full bg-linear-to-b from-purple-900 to-black" />
                        }
                    >
                        <AdaptiveVideo
                            basePath={env.R2_ASSETS_URL}
                            videoName="assets/videos/onboarding-background"
                            poster={assetPath("images/onboarding-poster.webp")}
                            className="h-full w-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                        />
                    </Suspense>
                </div>

                {/* Content overlay */}
                <div className="relative z-10 flex flex-col items-center justify-between py-12 px-6 h-full">
                    {/* Logo at top */}
                    <img
                        src={assetPath('logo/logo-red.svg')}
                        alt="What the f*** are you wearing"
                        className="w-64 h-auto"
                    />

                    {/* Centered text */}
                    <h2 className="text-white text-2xl font-medium text-center px-8">
                        Log in or create an account
                        <br />
                        to continue
                    </h2>

                    {/* Buttons at bottom */}
                    <div className="w-full space-y-3">
                        <button
                            onClick={handleLogin}
                            className="w-full py-4 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 text-white font-medium text-lg"
                        >
                            Log in
                        </button>
                        <button
                            onClick={handleSignup}
                            className="w-full py-4 rounded-xl bg-white text-black font-medium text-lg"
                        >
                            Create an account
                        </button>
                    </div>
                </div>
            </div>
        </Drawer>
    )
}
